// The Number format control (ARCH-C2-PRESENTATION): edits INS's one strict NumberFormat object.
// A person changes only what keeps the number's meaning: decimals, digit grouping and, for a
// ratio, shown as a number or a percent. Locale, currency, unit and the empty-value style always
// come from the slot's current (or default) format and are never invented or converted here.
// A legacy workbook string is not a typed format: the control says it can't be changed here.
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy } from '@/i18n/ns/inspect';
import type { NumberFormat } from '../../../src/artifacts/presentation-values.mjs';

export type { NumberFormat };
export type FormatValueType = 'money' | 'ratio' | 'count' | 'number';
export type FormatGroup = 'shown' | 'grouping' | 'decimals';
/**
 * ARCH-PRESENTATION-ABSENCE: the canonical number's own formatting, the default when no format
 * is stored. It labels the default state only; it is never offered or sent as a value.
 */
export const SCALAR_NATIVE = 'scalar-native';

/** Which choices each value type gets; none of them converts currency, unit or scale of data. */
export const FORMAT_GROUPS: Record<FormatValueType, FormatGroup[]> = {
  money: ['grouping', 'decimals'],
  ratio: ['shown', 'decimals'],
  count: ['grouping', 'decimals'],
  number: ['decimals'],
};
export const DECIMALS = ['0', '1', '2'] as const;
const SAMPLE: Record<FormatValueType, number> = {
  money: 12345.6,
  ratio: 0.256,
  count: 12345,
  number: 1234.5,
};

/** The format with one choice made; everything else is the base's own. */
export function withChoice(base: NumberFormat, group: FormatGroup, choice: string): NumberFormat {
  if (group === 'decimals') {
    const digits = Number(choice);
    return { ...base, minimumFractionDigits: digits, maximumFractionDigits: digits };
  }
  if (group === 'grouping') return { ...base, useGrouping: choice === 'on' };
  // Shown as: only between a plain number and a percent of a fraction; never a currency or unit.
  if (base.style !== 'decimal' && base.style !== 'percent') return base;
  return choice === 'percent'
    ? { ...base, style: 'percent', scale: 'fraction-to-percent' }
    : { ...base, style: 'decimal', scale: 'identity' };
}

/** Which choice of a group the format shows, or null when none matches exactly. */
export function choiceOf(format: NumberFormat, group: FormatGroup): string | null {
  if (group === 'decimals')
    return format.minimumFractionDigits === format.maximumFractionDigits
      ? String(format.minimumFractionDigits)
      : null;
  if (group === 'grouping') return format.useGrouping ? 'on' : 'off';
  if (format.style === 'decimal') return 'number';
  if (format.style === 'percent') return 'percent';
  return null;
}

/** Structural equality: two formats are the same when every field is (never by reference). */
export function sameFormat(a: NumberFormat, b: NumberFormat): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof NumberFormat>;
  for (const key of keys) if (a[key] !== b[key]) return false;
  return true;
}

/** A sample of the format, as the interface would show it; null if Intl refuses it. */
export function sample(format: NumberFormat, valueType: FormatValueType): string | null {
  try {
    const { locale, nullStyle: _nullStyle, scale: _scale, ...options } = format;
    return new Intl.NumberFormat(locale, options).format(SAMPLE[valueType]);
  } catch {
    return null;
  }
}

/**
 * `base` is the slot's current format, or its effective default when none is stored
 * (`isDefault`). With a mixed selection there is no single base: choices show unchecked and
 * the sheet applies the chosen one to each target's own format (`withChoice`).
 */
export function NumberFormatControl({
  base,
  seed = null,
  chosen = {},
  clearing = null,
  isDefault = false,
  mixed = false,
  unsupported = false,
  valueType,
  disabled = false,
  onChoose,
}: {
  base: NumberFormat | typeof SCALAR_NATIVE | null;
  /**
   * For a scalar-native default: ART's proposed starting format (bound to the scalar's own
   * metadata and trusted locale). It is where an edit starts, never shown as the current value.
   */
  seed?: NumberFormat | null;
  /**
   * Choices made in this edit and not yet applied: they show as checked (even with a mixed
   * selection) and the sample previews them, so re-choosing the original value reverts.
   */
  chosen?: Partial<Record<FormatGroup, string>>;
  /** A pending Reset: the registered default it will leave (shown as pending, never written). */
  clearing?: NumberFormat | typeof SCALAR_NATIVE | null;
  isDefault?: boolean;
  mixed?: boolean;
  unsupported?: boolean;
  valueType: FormatValueType;
  disabled?: boolean;
  onChoose: (group: FormatGroup, choice: string) => void;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  // From the number's own formatting an edit starts at ART's seed; without one nothing is
  // offered, because inventing a locale or currency is not an option.
  if (base === SCALAR_NATIVE && !mixed && !seed)
    return (
      <div className="pc-inspect-format" data-testid="inspect-number-format">
        <p
          className="pc-inspect-pres-current"
          data-kind="default"
          data-testid="inspect-format-sample"
        >
          {formatCopy(copy.defaultValue, { value: copy.formatScalarNative })}
        </p>
      </div>
    );
  if (unsupported || (!base && !mixed))
    return (
      <p className="pc-inspect-note" data-testid="inspect-format-unsupported">
        {copy.formatUnsupported}
      </p>
    );
  const native = base === SCALAR_NATIVE;
  const typed = native ? seed : base;
  const groups = FORMAT_GROUPS[valueType].filter(
    (g) => g !== 'shown' || mixed || (typed && choiceOf(typed, 'shown') !== null),
  );
  // No generic sample of the format (DES1 #3300): the pending choice shows checked, and the
  // actual before → after comes from the prepared change. The value line says only Mixed, or
  // the default when nothing is stored, with Reset on its right.
  const pendingDefault = clearing
    ? clearing === SCALAR_NATIVE
      ? copy.formatScalarNative
      : sample(clearing, valueType)
    : null;
  const defaultLabel =
    pendingDefault ??
    (mixed || Object.keys(chosen).length
      ? null
      : native
        ? copy.formatScalarNative
        : isDefault && typed
          ? sample(typed, valueType)
          : null);
  const options: Record<FormatGroup, { id: string; text: string }[]> = {
    shown: [
      { id: 'number', text: copy.shownNumber },
      { id: 'percent', text: copy.shownPercent },
    ],
    grouping: [
      { id: 'on', text: copy.groupOn },
      { id: 'off', text: copy.groupOff },
    ],
    decimals: DECIMALS.map((d) => ({ id: d, text: d })),
  };
  const titles: Record<FormatGroup, string> = {
    shown: copy.shownAs,
    grouping: copy.grouping,
    decimals: copy.decimals,
  };
  return (
    <div className="pc-inspect-format" data-testid="inspect-number-format">
      {((mixed && !clearing) || defaultLabel) && (
        <div className="pc-inspect-format-line">
          {mixed && !clearing ? (
            <span className="pc-inspect-mixed">{copy.mixed}</span>
          ) : (
            defaultLabel && (
              <span
                className="pc-inspect-pres-current"
                data-kind={clearing ? 'pending-default' : 'default'}
                data-testid="inspect-format-sample"
              >
                {formatCopy(copy.defaultValue, { value: defaultLabel })}
              </span>
            )
          )}
        </div>
      )}
      {groups.map((group) => {
        // A pending choice shows checked; otherwise the current value (never the seed, which is
        // where an edit starts, not the current value).
        // A pending Reset checks nothing: the default it leaves is shown on the value line.
        const now = clearing
          ? null
          : (chosen[group] ?? (typed && !mixed && !native ? choiceOf(typed, group) : null));
        const row = (
          <div key={group} className="pc-inspect-seg" role="radiogroup" aria-label={titles[group]}>
            <span className="pc-inspect-seg-label" aria-hidden="true">
              {titles[group]}
            </span>
            {options[group].map((o) => (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={now === o.id}
                data-choice={`${group}:${o.id}`}
                disabled={disabled}
                onClick={() => {
                  if (now !== o.id) onChoose(group, o.id);
                }}
              >
                {o.text}
              </button>
            ))}
          </div>
        );
        return row;
      })}
    </div>
  );
}
